import type { QuoteDocument, QuoteFile } from 'paintscout'

const VIDEO_FORMATS = ['mp4', 'mov', 'webm', 'm4v', 'ogv', 'avi', 'mkv']

/**
 * Only PDFs and videos are ruled out. Provider photos (e.g. CompanyCam) can have no format and
 * no file extension, so requiring a known image type would skip real photos.
 */
function isPdfOrVideo(file: QuoteFile): boolean {
  const format = file.format?.toLowerCase() ?? ''
  const type = file.type?.toLowerCase() ?? ''
  return (
    format === 'pdf' ||
    type === 'application/pdf' ||
    format.startsWith('video') ||
    type.startsWith('video') ||
    VIDEO_FORMATS.includes(format)
  )
}

function isUsable(file: QuoteFile | undefined): file is QuoteFile {
  return Boolean(file?.src) && !file.deleted && !isPdfOrVideo(file)
}

/** The banner renders in the customer view, so a crew-only or hidden photo must never show there. */
function isCustomerVisible(file: QuoteFile): boolean {
  return file.visibility === 'visible' || file.visibility === 'customer'
}

/**
 * The photo a presentation Banner shows when "Use home image from estimate" is on (#25097):
 *
 * 1. The estimate's home image (`quote.homeImage`): an uploaded photo, or a customer-visible photo
 *    on the estimate.
 * 2. Otherwise, the first customer-visible photo on the estimate, walking areas, line items and
 *    groups in `order.area` order. This follows v1's cover (`PresentationPages.tsx`), except that
 *    v1 checks only the first file of the first item that has files, shows nothing when that file
 *    is not 'visible', and ignores 'customer' visibility.
 * 3. Otherwise `null`, and the banner shows its own template image.
 */
export function getQuoteHomeImage(args: { quote: QuoteDocument }): QuoteFile | null {
  const { quote } = args
  if (!quote) {
    return null
  }

  const files = quote.files ?? {}
  const { homeImage } = quote

  if (homeImage?.file && isUsable(homeImage.file)) {
    return homeImage.file
  }
  const markedFile = homeImage?.fileKey ? files[homeImage.fileKey] : undefined
  if (isUsable(markedFile) && isCustomerVisible(markedFile)) {
    return markedFile
  }

  for (const orderItem of quote.order?.area ?? []) {
    const item = quote.areas?.[orderItem.key] ?? quote.lineItems?.[orderItem.key] ?? quote.groups?.[orderItem.key]
    for (const key of item?.files ?? []) {
      const file = files[key]
      if (isUsable(file) && isCustomerVisible(file)) {
        return file
      }
    }
  }

  return null
}
